import { SystemProps, Flex, Box } from '@storyofams/react-ui'
import { Image as ToolkitImage } from '@storyofams/storyblok-toolkit'
import { useEffect, useState, useMemo } from 'react'
import { Text, Button, Heading, FixedRatio, Select } from '~components'
import {
  ThemeBoxHeaderTexture,
  ThemeBoxHeaderTextureMobile,
} from './Textures/ThemeBoxHeaderTextures'

export const ThemeBoxHeader = ({
  title,
  description,
  fontColor,
  image,
  imageMobile,
  useFullHeightImage,
  backgroundColor,
  productSpecs,
  textBlockBackgroundColor,
  options,
  setSelectedOption,
  selectedVariant,
  isOptionDisabled,
  CheckoutButton,
  useBadge,
  badgeImage,
  badgePosMX,
  badgePosMY,
  badgePosX,
  badgePosY,
  badgeWidth,
  OneVariant,
}) => {
  return (
    <Flex
      marginTop="0 !important"
      width="100%"
      backgroundColor={backgroundColor ? backgroundColor : 'auto'}
      position={'relative'}
      flexDirection={['column', 'row']}
    >
      <Flex width={['100%', '50%']}>
        {!!image && (
          <Flex position={'relative'} width="100%">
            {useFullHeightImage ? (
              <Box display={['none', 'block']} width="100%">
                <Flex height={'100%'} width={'100%'}>
                  <ToolkitImage
                    src={image?.filename}
                    alt={image?.alt}
                    fluid={1200}
                    fit="cover"
                    width={'100%'}
                  />
                </Flex>
              </Box>
            ) : (
              <Box
                display={['none', 'block']}
                position={'absolute'}
                width="100%"
              >
                <FixedRatio
                  width={['100%']}
                  ratio={[[1, 1]]}
                  maxWidth={['100%', '100%']}
                >
                  <Flex
                    zIndex={'1'}
                    top={['0', '0', '-5%']}
                    height={['100%', '100%']}
                    width={['100%', '100%']}
                    position={'absolute'}
                  >
                    <ToolkitImage
                      src={image?.filename}
                      alt={image?.alt}
                      fluid={1200}
                      fit="contain"
                      width={'100%'}
                    />
                  </Flex>
                </FixedRatio>
              </Box>
            )}
            <Box display={['block', 'none']} width="100%">
              <FixedRatio
                width={['100%']}
                ratio={[[392, 350]]}
                maxWidth={['100%', '100%']}
              >
                <Flex
                  zIndex={'1'}
                  top={'0'}
                  height={['100%', '100%']}
                  width={['100%', '100%']}
                  position={'absolute'}
                >
                  <ToolkitImage
                    src={
                      !!imageMobile?.filename
                        ? imageMobile?.filename
                        : image?.filename
                    }
                    alt={imageMobile?.alt}
                    fluid={1200}
                    fit="cover"
                    width={'100%'}
                  />
                </Flex>
              </FixedRatio>
            </Box>
            <Box
              display={['block', 'none']}
              position={'absolute'}
              bottom={'0'}
              width={'100%'}
              backgroundColor={
                textBlockBackgroundColor ? textBlockBackgroundColor : 'auto'
              }
            >
              <ThemeBoxHeaderTextureMobile
                backgroundTextureColor={backgroundColor}
              />
            </Box>
          </Flex>
        )}
      </Flex>
      <Flex
        width={['100%', '50%']}
        backgroundColor={
          textBlockBackgroundColor ? textBlockBackgroundColor : 'auto'
        }
        position={'relative'}
      >
        {!!badgeImage?.filename && useBadge && (
          <Box
            position="absolute"
            right={[`${badgePosMX}%`, `${badgePosX}%`]}
            top={[`${badgePosMY}px`, `${badgePosY}px`]}
            width={`${badgeWidth}px`}
            zIndex={2}
          >
            <ToolkitImage
              width="100%"
              src={badgeImage?.filename}
              alt={badgeImage?.alt}
              fluid={500}
            />
          </Box>
        )}
        <Flex
          width={['100%', '100%', '76%']}
          flexDirection={'column'}
          justifyContent={'space-between'}
          p={[3, 5]}
        >
          <Box>
            {!!title && (
              <Heading
                as="h2"
                variant="sh2"
                mt={[0, 3]}
                width={'100%'}
                color={!!fontColor ? fontColor : 'initial'}
              >
                {title}
              </Heading>
            )}
            {!!description && (
              <Text
                mt={3}
                fontSize={[2.5]}
                variant={'uppercase'}
                color={!!fontColor ? fontColor : 'initial'}
              >
                {description}
              </Text>
            )}
          </Box>
          <Box>
            {!!productSpecs && productSpecs.length > 0 && (
              <Flex
                mb={[3]}
                mt={'45px'}
                gap={['0', 5]}
                width={'100%'}
                justifyContent={['space-between', 'normal']}
              >
                {productSpecs.map(spec => {
                  return (
                    <Text
                      key={spec?._uid}
                      variant={'uppercase'}
                      whiteSpace={'pre'}
                      color={!!fontColor ? fontColor : 'initial'}
                    >
                      {spec?.text}
                    </Text>
                  )
                })}
              </Flex>
            )}
            <Flex
              gap={2}
              width={'100%'}
              flexDirection={['column', 'column', 'row', 'row']}
              alignItems={'center'}
            >
              {options && options?.length > 1 ? (
                <Select
                  width={['100%', '100%', '200px']}
                  minWidth={['100%', '100%', '200px']}
                  options={options}
                  onChange={({ value }) => setSelectedOption(value)}
                  value={
                    !!options && !!selectedVariant
                      ? options.find(
                          option => option.value === selectedVariant.id,
                        )
                      : null
                  }
                  isOptionDisabled={isOptionDisabled}
                  isSearchable={false}
                  customStyles={{
                    '.react-select': {
                      '&__single-value p': {
                        fontWeight: '700',
                        textTransform: 'uppercase',
                      },
                      '&__indicators svg': {
                        fill: `${'black'}`,
                      },
                      '&__control': {
                        borderRadius: '0',
                        borderWidth: '1.5px',
                        borderColor: `${fontColor ? fontColor : 'black'}`,
                        fontSize: 2,
                        fontWeight: '700',
                        height: '45px',
                        justifyContent: 'center',
                        '&:hover': {
                          cursor: 'pointer',
                          bg: 'grey300',
                        },
                        '&--menu-is-open': {
                          borderBottomLeftRadius: 0,
                          borderBottomRightRadius: 0,
                        },
                      },
                      '&__menu': {
                        zIndex: 2,
                        my: 0,
                        borderRadius: '0',
                        borderTopLeftRadius: 0,
                        borderTopRightRadius: 0,
                        borderWidth: '1.5px',
                        borderStyle: 'solid',
                        borderColor: `${fontColor ? fontColor : 'black'}`,
                        borderTop: 'none',
                        boxShadow: 'none',
                        overflow: 'hidden',

                        '.react-select__menu-list': {
                          p: 0,
                        },
                        '.react-select__menu-list p': {
                          textTransform: 'uppercase',
                        },
                        '.react-select__option': {
                          px: 1,
                          py: 0.5,
                          mb: 0.2,
                          '&:hover': {
                            cursor: 'pointer',
                          },
                          '&--is-disabled': {
                            color: 'grey400',
                            cursor: 'not-allowed',
                          },
                        },
                      },
                    },
                  }}
                />
              ) : (
                <OneVariant textColor={!!fontColor ? fontColor : 'initial'} />
              )}
              <CheckoutButton />
            </Flex>
          </Box>
        </Flex>
        <Flex width={['0', '0', '24%']}>
          <Box width={'auto'} height={'auto'}>
            <ThemeBoxHeaderTexture backgroundTextureColor={backgroundColor} />
          </Box>
        </Flex>
      </Flex>
    </Flex>
  )
}
